import {
  Badge,
  Button,
  Card,
  Segmented,
  Select,
  Table,
  Tag,
  Typography,
  Input,
  DatePicker,
} from "antd";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QuotationService } from "../../Services/Manage/QuotationService";
import useMessage from "antd/es/message/useMessage";
import { UserService } from "../../Services/Manage/UserService";

const QuotationPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [segment, setSegment] = useState("Active");
  const [message, msgContext] = useMessage();
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [dateFilterType, setDateFilterType] = useState("updated_on"); // 👈 Default type

  /** 🔹 Fetch all quotations */
  const getAllQuo = async () => {
    try {
      const user = await UserService.getOmeUser();
      const requests = await QuotationService.getAllQuotationByUID(user?.uid);
      const allData = requests.data || [];
      setData(allData);
      applyAllFilters(allData, segment, searchText, dateRange, dateFilterType);
    } catch (error) {
      console.error("Error fetching quotations:", error);
    }
  };

  useEffect(() => {
    getAllQuo();
  }, []);

  /** 🔹 Apply filtering */
  const applyAllFilters = (allData, seg, search, range, dateType) => {
    let result = allData;

    // Filter by segment
    if (seg) {
      result = result.filter(
        (item) => item.status?.toLowerCase() === seg.toLowerCase()
      );
    }

    // Filter by search
    if (search) {
      const lower = search.toLowerCase();
      result = result.filter(
        (item) =>
          item.customer_name?.toLowerCase().includes(lower) ||
          item.qu_no?.toLowerCase().includes(lower) ||
          item.eq_no?.toLowerCase().includes(lower)
      );
    }

    // Filter by date range (Quotation Date, Added On, or Updated On)
    if (range && range.length === 2) {
      const [start, end] = range;
      result = result.filter((item) => {
        const selectedDate = new Date(item[dateType]); // 👈 Dynamic field
        if (isNaN(selectedDate)) return false;
        return (
          selectedDate >= start.startOf("day").toDate() &&
          selectedDate <= end.endOf("day").toDate()
        );
      });
    }

    // Sort by updated_on (latest first)
    result.sort((a, b) => new Date(b.updated_on) - new Date(a.updated_on));
    setFilteredData(result);
  };

  /** 🔹 Handlers */
  const handleSegmentChange = (val) => {
    setSegment(val);
    applyAllFilters(data, val, searchText, dateRange, dateFilterType);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    applyAllFilters(data, segment, value, dateRange, dateFilterType);
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    applyAllFilters(data, segment, searchText, range, dateFilterType);
  };

  const handleDateFilterTypeChange = (value) => {
    setDateFilterType(value);
    applyAllFilters(data, segment, searchText, dateRange, value);
  };

  /** 🔹 Status counts */
  const statusCounts = data.reduce((acc, cur) => {
    const key = cur.status || "Unknown";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const styles = {
    badge: {
      backgroundColor: "#1b1660ff",
      fontSize: 14,
      minWidth: 24,
      height: 24,
      lineHeight: "24px",
      marginLeft: 10,
    },
  };

  /** 🔹 Table Columns */
  const columns = [
    {
      title: "SI No",
      align: "center",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Quotation Date",
      dataIndex: "date",
      render: (text) => (text ? new Date(text).toLocaleDateString() : "-"),
    },
    {
      title: "Quotation No",
      dataIndex: "qu_no",
    },
    {
      title: "Enquiry No",
      dataIndex: "eq_no",
    },
    {
      title: "Customer Name",
      dataIndex: "customer_name",
    },
    {
      title: "Customer GST",
      dataIndex: "cus_gst",
    },
    {
      title: "Customer Phone",
      dataIndex: "customer_phone",
    },
    {
      title: "Collected / Pending",
      key: "payment_summary",
      render: (_, record) => {
        const payments = record.payments || [];
        const totalCollected = payments.reduce(
          (sum, p) => sum + Number(p.paid_amt || 0),
          0
        );
        const grandTotal = Number(payments[0]?.grand_total || 0);
        const pending = grandTotal - totalCollected;

        return (
          <>
            <div>
              Collected: <strong>{totalCollected}</strong>
            </div>
            <div>
              Pending: <strong>{pending}</strong>
            </div>
          </>
        );
      },
    },
    {
      title: "Payment Status",
      align: "center",
      render: (_, record) => {
        const payments = record.payments || [];
        const isPaid = payments.some((p) => p.status === "Paid");
        return (
          <Tag
            style={{ fontSize: "16px", padding: "6px 12px" }}
            color={isPaid ? "green" : "orange"}
          >
            {isPaid ? "Paid" : "Pending"}
          </Tag>
        );
      },
    },
    {
      title: "Added On",
      dataIndex: "added_on",
      render: (text) => new Date(text).toLocaleString(),
    },
    {
      title: "Updated On",
      dataIndex: "updated_on",
      render: (text) => new Date(text).toLocaleString(),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status, record) => (
        <Select
          size="large"
          defaultValue={status}
          style={{ width: "100px" }}
          options={[
            { label: "Active", value: "Active" },
            { label: "Draft", value: "Draft" },
            { label: "Archive", value: "Archive" },
          ]}
          onChange={async (value) => {
            try {
              const payload = { status: value };
              const response = await QuotationService.patchQuotation(
                payload,
                record.qu_no
              );
              if (response.status) {
                message.success(response.message);
                getAllQuo();
              } else {
                message.warning(response.message);
              }
            } catch (error) {
              console.error(error);
            }
          }}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      render: (_, record) => (
        <Button
          onClick={() =>
            navigate("/quotation/edit", { state: { qu_no: record.qu_no } })
          }
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <Card
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography.Title level={4} style={{ margin: 0 }}>
            Manage Quotation
          </Typography.Title>

          <Button
            onClick={() => navigate("/quotation/create")}
            size="large"
            type="primary"
          >
            Create Quotation
          </Button>
        </div>
      }
    >
      {msgContext}

      {/* 🔹 Segment Tabs */}
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
        <Segmented
          value={segment}
          size="large"
          onChange={handleSegmentChange}
          options={[
            {
              label: (
                <span style={{ color: "green" }}>
                  Active <Badge count={statusCounts["Active"] || 0} style={styles.badge} />
                </span>
              ),
              value: "Active",
            },
            {
              label: (
                <span style={{ color: "red" }}>
                  Archive <Badge count={statusCounts["Archive"] || 0} style={styles.badge} />
                </span>
              ),
              value: "Archive",
            },
            {
              label: (
                <span style={{ color: "blue" }}>
                  Draft <Badge count={statusCounts["Draft"] || 0} style={styles.badge} />
                </span>
              ),
              value: "Draft",
            },
          ]}
          style={{ fontSize: 16, fontWeight: 500 }}
        />
      </div>

      {/* 🔹 Search + Date Range */}
      <Card style={{ marginBottom: 16 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            flexWrap: "wrap",
          }}
        >
          <Input
            allowClear
            placeholder="Search Quotations..."
            size="large"
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            style={{ width: 280 }}
          />

          <Select
            size="large"
            placeholder="Date filter type"
            allowClear
            value={dateFilterType}
            onChange={handleDateFilterTypeChange}
            style={{ width: 200 }}
            options={[
              { label: "Quotation Date", value: "date" },
              { label: "Added On", value: "added_on" },
              { label: "Updated On", value: "updated_on" },
            ]}
          />

          <DatePicker.RangePicker
            size="large"
            onChange={handleDateRangeChange}
            value={dateRange}
            format="DD-MM-YYYY"
            style={{ width: 280 }}
          />
        </div>
      </Card>

      {/* 🔹 Table */}
      <Table
        scroll={{ x: "max-content" }}
        dataSource={filteredData}
        columns={columns}
        rowKey="qu_no"
        pagination={{ pageSize: 10 }}
      />
    </Card>
  );
};

export default QuotationPage;
